import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import type { LocationLabel, LocationLabelsResponse } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { Alert, ErrorAlert } from '../components/ui'

/** Étiquettes imprimables A4 : QR (URL seule) + nom du rangement. */
export function LabelsPage() {
  const { t } = useTranslation()
  const { current } = useHousehold()
  const [params] = useSearchParams()
  const ids = useMemo(
    () =>
      (params.get('ids') ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [params],
  )

  const labels = useQuery({
    queryKey: ['labels', current?.id, ids.join(',')],
    enabled: Boolean(current?.id) && ids.length > 0,
    queryFn: () =>
      api.get<LocationLabelsResponse>(
        `/households/${current!.id}/labels?ids=${encodeURIComponent(ids.join(','))}`,
      ),
  })

  if (!current) return <p role="status">{t('common.loading')}</p>

  if (ids.length === 0) {
    return (
      <>
        <h1>{t('labels.title')}</h1>
        <Alert kind="warning">{t('labels.noSelection')}</Alert>
        <p>
          <Link className="btn" to="/storage">
            {t('inventory.seeStorage')}
          </Link>
        </p>
      </>
    )
  }

  return (
    <>
      <div className="no-print row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>{t('labels.title')}</h1>
          <p className="muted">{t('labels.hint', { domain: labels.data?.publicUrl ?? '…' })}</p>
        </div>
        <div className="row">
          <Link className="btn secondary" to="/storage">
            {t('common.back')}
          </Link>
          <button type="button" className="btn" onClick={() => window.print()}>
            {t('labels.print')}
          </button>
        </div>
      </div>
      <ErrorAlert error={labels.error} />
      {labels.isLoading && <p role="status">{t('common.loading')}</p>}
      {labels.data && (
        <ul className="label-sheet">
          {labels.data.items.map((item) => (
            <li key={item.id}>
              <LabelCard label={item} />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function LabelCard({ label }: { label: LocationLabel }) {
  const { t } = useTranslation()
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void QRCode.toDataURL(label.url, { margin: 1, width: 220, errorCorrectionLevel: 'M' }).then((url) => {
      if (!cancelled) setSrc(url)
    })
    return () => {
      cancelled = true
    }
  }, [label.url])

  return (
    <article className="label-card">
      {src ? (
        <img className="qr" src={src} alt={t('labels.qrAlt', { name: label.name })} width={220} height={220} />
      ) : (
        <span className="thumb-placeholder" style={{ width: 220, height: 220 }} aria-hidden />
      )}
      <strong className="label-card-name">{label.name}</strong>
      <span className="muted label-card-url">{label.url}</span>
    </article>
  )
}
